import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { Check, Copy, Eye, Flag, Handshake, Home, Plus, RotateCcw, Search, Share2 } from 'lucide-react';
import {
  RATED_CLOCKS,
  isValidRoomCode,
  type Color,
  type PublicMove,
  type PublicRoomState,
} from '../../shared/protocol.ts';
import { useAuth } from '../app/auth.ts';
import { useDisplayName } from '../app/identity.ts';
import { usePreferences } from '../app/preferences.ts';
import { navigate, setLeaveGuard } from '../app/router.ts';
import { Link } from '../components/Link.tsx';
import { Board } from '../components/Board.tsx';
import { PersonAvatar } from '../components/Brand.tsx';
import { ConfirmDialog } from '../components/Dialog.tsx';
import { GameLayout } from '../components/GameLayout.tsx';
import { MoveList } from '../components/MoveList.tsx';
import { PlayerBar, YourTurnBadge } from '../components/PlayerBar.tsx';
import { ResultDialog } from '../components/ResultDialog.tsx';
import { VsIntro } from '../components/VsIntro.tsx';
import { MovesFold, TurnBanner, type TurnKind } from '../components/TurnBanner.tsx';
import { timeControlText } from '../i18n/format.ts';
import { materialSummary, positionsAfter, type MoveLike } from '../lib/chess.ts';
import { sound, soundForMove } from '../lib/sound.ts';
import { useOnlineRoom, type OnlineRoom } from '../online/useOnlineRoom.ts';
import { useHistoryCursor, useResultDialog, useTransient } from '../hooks/useGameView.ts';
import type { MessageKey } from '../i18n/messages.ts';

export function RoomPage({ code }: { code: string }) {
  if (!isValidRoomCode(code)) return <RoomUnavailable kind="notFound" />;
  return <Room code={code} />;
}

/** Seconds of "you vs them" before a game started from a shared link. */
const LINK_INTRO_S = 6;
const introKey = (code: string) => `outchess:intro:${code}`;

function introSeen(code: string): boolean {
  try {
    return window.sessionStorage.getItem(introKey(code)) === '1';
  } catch {
    return false;
  }
}

function Room({ code }: { code: string }) {
  const { t } = usePreferences();
  const name = useDisplayName().name;
  const room = useOnlineRoom(code, name);
  const [introDone, setIntroDone] = useState(() => introSeen(code));
  const finishIntro = useCallback(() => {
    try {
      window.sessionStorage.setItem(introKey(code), '1');
    } catch {
      // fine: at worst the face-off shows again after a refresh
    }
    setIntroDone(true);
  }, [code]);

  if (room.phase === 'not_found') return <RoomUnavailable kind="notFound" />;
  if (room.phase === 'closed') return <RoomUnavailable kind="closed" />;
  if (!room.state) {
    return (
      <div className="center-state" role="status">
        <span className="spinner" />
        <p>{t('room.connecting')}</p>
      </div>
    );
  }
  if (room.state.status === 'waiting') return <WaitingRoom code={code} room={room} />;

  // A game started from a shared link opens with the same face-off as matchmaking
  // (matchmade games already had theirs before reaching the room). It ends early
  // once a move is on the board, e.g. after a refresh mid-countdown.
  const s = room.state;
  const me = room.role === 'w' || room.role === 'b' ? room.role : null;
  const opponentSeat = me ? s.players[me === 'w' ? 'b' : 'w'] : null;
  const mySeat = me ? s.players[me] : null;
  if (!introDone && me && mySeat && opponentSeat && s.status === 'playing' && !s.matched && s.gameNumber === 1 && s.moves.length === 0) {
    return (
      <VsIntro
        title={t('room.introTitle')}
        me={mySeat}
        myColor={me}
        opponent={opponentSeat}
        seconds={LINK_INTRO_S}
        onDone={finishIntro}
      />
    );
  }
  return <OnlineGame room={room} state={room.state} />;
}

function RoomUnavailable({ kind }: { kind: 'notFound' | 'closed' }) {
  const { t } = usePreferences();
  return (
    <div className="center-state">
      <span className="result-icon emoji" aria-hidden="true">
        🙈
      </span>
      <h1 className="page-title" style={{ fontSize: 34 }}>
        {t(`room.${kind}.title`)}
      </h1>
      <p>{t(`room.${kind}.desc`)}</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Link href="/" className="btn">
          <Home size={22} /> {t('room.home')}
        </Link>
        <Link href="/friend" className="btn btn-green">
          <Plus size={22} /> {t('room.newRoom')}
        </Link>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ waiting

function WaitingRoom({ code, room }: { code: string; room: OnlineRoom }) {
  const { t } = usePreferences();
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/r/${code}`;
  const tc = timeControlText(room.state?.timeControl ?? null, t);
  const canShare = typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked: the link stays selectable in the field
    }
  };

  return (
    <div className="card waiting">
      <span className="waiting-art emoji" aria-hidden="true">
        ⏳
      </span>
      <div className="lobby-card-head">
        <h1 className="lobby-card-title" style={{ fontSize: 34, justifyContent: 'center' }}>
          {t('room.waiting.title')}
        </h1>
        <p className="lobby-card-desc">{t('room.waiting.desc')}</p>
      </div>

      <div className="field" style={{ justifyItems: 'center' }}>
        <span className="field-label">{t('room.code')}</span>
        <div className="room-code" aria-label={code}>
          {code.split('').map((ch, i) => (
            <span key={i}>{ch}</span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span className="badge">⏱ {tc}</span>
          {room.role === 'w' || room.role === 'b' ? <span className="badge">{t(`color.${room.role}`)}</span> : null}
        </div>
      </div>

      <div className="share-row">
        <input className="input" readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label={t('room.copyLink')} />
        <button type="button" className="btn btn-primary" onClick={copy}>
          {copied ? <Check size={22} strokeWidth={3} /> : <Copy size={22} />}
          {copied ? t('room.copied') : t('room.copyLink')}
        </button>
        {canShare && (
          <button
            type="button"
            className="btn btn-blue"
            onClick={() => navigator.share({ title: 'Chessrokko', text: t('room.shareText'), url: link }).catch(() => undefined)}
          >
            <Share2 size={22} /> {t('room.share')}
          </button>
        )}
      </div>

      <div className="waiting-indicator" role="status">
        <span className="spinner" style={{ width: 24, height: 24 }} />
        {room.phase === 'reconnecting' ? t('room.reconnecting') : t('room.waitingIndicator')}
      </div>

      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => {
          room.send({ type: 'close' });
          navigate('/friend');
        }}
      >
        {t('room.cancel')}
      </button>
    </div>
  );
}

// --------------------------------------------------------------------- game

const ERROR_MESSAGES: Partial<Record<string, MessageKey>> = {
  illegal_move: 'error.illegal_move',
  not_your_turn: 'error.not_your_turn',
};

function OnlineGame({ room, state }: { room: OnlineRoom; state: PublicRoomState }) {
  const { t } = usePreferences();
  const me: Color | null = room.role === 'w' || room.role === 'b' ? room.role : null;
  const opponent: Color | null = me ? (me === 'w' ? 'b' : 'w') : null;

  const [flipped, setFlipped] = useState(false);
  const base: Color = me ?? 'w';
  const orientation: Color = flipped ? (base === 'w' ? 'b' : 'w') : base;
  const [confirmResign, setConfirmResign] = useState(false);

  const moves = room.moves;
  const fens = useMemo(() => {
    try {
      return positionsAfter(moves);
    } catch {
      return positionsAfter(state.moves);
    }
  }, [moves, state.moves]);

  const livePly = fens.length - 1;
  const turn: Color = livePly % 2 === 0 ? 'w' : 'b';
  const inCheck = useMemo(() => new Chess(fens[livePly]).inCheck(), [fens, livePly]);
  const history = useHistoryCursor(livePly);
  const { ply, isLive } = history;
  const shownFen = fens[ply];
  const material = useMemo(() => materialSummary(shownFen), [shownFen]);
  const playing = state.status === 'playing';
  const resultDialog = useResultDialog(
    state.status === 'ended' && state.result ? `${state.gameNumber}:${state.result.reason}` : null,
  );
  const error = useTransient(room.lastError, 2500);

  // The opponent dropped: show the forfeit countdown, then say so when they return.
  const opponentAway = useForfeitCountdown(state, opponent ?? 'b', room.receivedAt);
  const opponentConnected = opponent !== null && Boolean(state.players[opponent]?.connected);
  const [presence, setPresence] = useState({ connected: opponentConnected, awayInPlay: !opponentConnected && playing });
  const [opponentBack, setOpponentBack] = useState<{ n: number } | null>(null);
  if (opponent !== null && presence.connected !== opponentConnected) {
    setPresence({ connected: opponentConnected, awayInPlay: !opponentConnected && playing });
    if (opponentConnected && presence.awayInPlay && playing) setOpponentBack({ n: (opponentBack?.n ?? 0) + 1 });
  }
  const backNotice = useTransient(opponentBack, 3500);
  const toast = error
    ? t(ERROR_MESSAGES[error.code] ?? 'error.generic')
    : backNotice
      ? t('room.opponentBack')
      : null;

  // Leaving a live game (closing the tab, Back, an in-app link) asks first:
  // the player loses by forfeit unless they return in time.
  const graceSeconds = Math.round(state.reconnectGraceMs / 1000);
  const guarding = me !== null && playing;
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  useEffect(() => {
    if (!guarding) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    setLeaveGuard(setLeaveTo);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      setLeaveGuard(null);
    };
  }, [guarding]);

  // Sounds for moves made by the other side, and for game start/end.
  const seenPly = useRef(livePly);
  useEffect(() => {
    if (livePly > seenPly.current) {
      const last = moves[livePly - 1];
      const moverIsMe = me !== null && (livePly % 2 === 1 ? 'w' : 'b') === me;
      if (!moverIsMe && last) {
        const made = new Chess(fens[livePly - 1]).move({ from: last.from, to: last.to, promotion: last.promotion });
        sound.play(soundForMove(made));
      }
    }
    seenPly.current = livePly;
  }, [livePly, moves, fens, me]);

  const { refresh: refreshAccount } = useAuth();
  const prevStatus = useRef(state.status);
  useEffect(() => {
    if (prevStatus.current !== state.status) {
      if (state.status === 'playing') sound.play('start');
      prevStatus.current = state.status;
    }
  }, [state.status]);

  // A rated result changes the rating shown in the header.
  const settled = state.rated && state.status === 'ended';
  useEffect(() => {
    if (settled) void refreshAccount();
  }, [settled, state.ratingChange, refreshAccount]);

  const onMove = (move: MoveLike) => {
    const chess = new Chess(fens[livePly]);
    let made;
    try {
      made = chess.move(move);
    } catch {
      return;
    }
    sound.play(soundForMove(made));
    const record: PublicMove = { san: made.san, from: made.from, to: made.to, ...(move.promotion ? { promotion: move.promotion } : {}) };
    room.move(record);
  };

  const bar = (color: Color) => {
    const player = state.players[color];
    const isSelf = color === me;
    const name = player?.name ?? t('room.opponent');
    return (
      <PlayerBar
        name={isSelf ? `${name} (${t('game.you')})` : name}
        avatar={<PersonAvatar name={name} />}
        color={color}
        captured={material.captured[color]}
        lead={material.lead[color]}
        isSelf={isSelf}
        rating={player?.rating ?? null}
        look={player?.look ?? null}
        clock={state.clocks ? { ms: state.clocks[color], running: state.clockRunning === color, anchorAt: room.receivedAt } : null}
        status={
          player && !isSelf ? (
            <PresenceBadge state={state} color={color} receivedAt={room.receivedAt} />
          ) : isSelf && isLive && playing && turn === me ? (
            <YourTurnBadge label={inCheck ? t('game.check') : t('game.yourTurn')} check={inCheck} />
          ) : null
        }
      />
    );
  };

  const drawOfferedToMe = playing && opponent !== null && state.drawOffer === opponent;
  const iOfferedDraw = playing && me !== null && state.drawOffer === me;
  const iWantRematch = me !== null && state.rematchRequests.includes(me);
  const opponentWantsRematch = opponent !== null && state.rematchRequests.includes(opponent);
  const tc = timeControlText(state.timeControl, t);
  const [turnKind, turnText]: [TurnKind, string] =
    state.status === 'ended' && state.result
      ? ['over', t(`reason.${state.result.reason}`)]
      : me === null
        ? ['wait', t('game.toMove', { color: t(`color.${turn}`) })]
        : turn !== me
          ? ['wait', t('game.theirTurn')]
          : inCheck
            ? ['check', t('game.checkYou')]
            : ['mine', t('game.yourTurn')];

  const notice =
    room.phase === 'reconnecting' ? (
      <div className="banner banner-danger" role="status">
        <span>{t('room.reconnecting')}</span>
        <span className="spinner" style={{ width: 24, height: 24 }} />
      </div>
    ) : me !== null && opponentAway !== null ? (
      <div className="banner banner-warning" role="alert">
        <span className="forfeit-count" aria-hidden="true">
          {opponentAway}
        </span>
        <span>{t('room.opponentAway', { seconds: opponentAway })}</span>
      </div>
    ) : null;

  // Matchmade games have no rematch: players go back to matchmaking with the same clock.
  const ratedClock = state.matchClock ?? (state.timeControl?.initialMs === RATED_CLOCKS.blitz.initialMs ? 'blitz' : 'rapid');
  const findNewButton = (
    <Link href={`/online?clock=${ratedClock}`} className="btn btn-green">
      <Search size={22} /> {t('room.findNew')}
    </Link>
  );
  const myRating = state.rated && me !== null ? { change: state.ratingChange?.[me] ?? null } : undefined;
  const nameOf = (color: Color) => {
    const name = state.players[color]?.name ?? t('room.opponent');
    return color === me ? `${name} (${t('game.you')})` : name;
  };
  const resultNames = { w: nameOf('w'), b: nameOf('b') };

  const rematchButton = state.matched ? findNewButton : (
    <button
      type="button"
      className="btn btn-green"
      onClick={() => room.send({ type: 'rematch' })}
      disabled={iWantRematch || !state.players.w?.connected || !state.players.b?.connected}
    >
      <RotateCcw size={22} />
      {iWantRematch ? t('room.rematchRequested') : opponentWantsRematch ? t('room.acceptRematch') : t('game.rematch')}
    </button>
  );

  return (
    <>
      <GameLayout
        turn={isLive && me !== null ? turnKind : undefined}
        notice={notice}
        top={bar(orientation === 'w' ? 'b' : 'w')}
        bottom={bar(orientation)}
        board={
          <Board
            fen={shownFen}
            orientation={orientation}
            movableColor={isLive && playing && room.phase === 'open' ? me : null}
            lastMove={ply > 0 ? moves[ply - 1] : null}
            historical={!isLive}
            onMove={onMove}
          />
        }
        panel={
          <>
            <div className="panel-header">
              <span className="panel-title">{state.rated ? `🏆 ${t('room.rated')}` : t('room.game', { number: state.gameNumber })}</span>
              <span className="badge">⏱ {tc}</span>
            </div>
            <div className="panel-section turn-section">
              <TurnBanner kind={turnKind} text={turnText} />
            </div>

            {(me === null || drawOfferedToMe || (!playing && opponentWantsRematch && !iWantRematch) || !isLive) && (
              <div className="panel-section panel-status">
                {me === null && <div className="banner">{t('room.spectating')}</div>}
                {drawOfferedToMe && (
                  <div className="banner" role="alert">
                    <span>{t('room.drawOffer')}</span>
                    <div className="banner-actions">
                      <button type="button" className="btn btn-sm" onClick={() => room.send({ type: 'decline_draw' })}>
                        {t('room.decline')}
                      </button>
                      <button type="button" className="btn btn-sm btn-green" onClick={() => room.send({ type: 'accept_draw' })}>
                        {t('room.accept')}
                      </button>
                    </div>
                  </div>
                )}
                {!playing && opponentWantsRematch && !iWantRematch && (
                  <div className="banner" role="alert">
                    {t('room.rematchOffered')}
                  </div>
                )}
                {!isLive && (
                  <div className="banner">
                    <span>{t('game.viewing')}</span>
                    <button type="button" className="btn btn-sm btn-blue" onClick={history.backToLive}>
                      {t('game.backToLive')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {me !== null && (
              <div className="panel-section panel-status game-actions">
                {playing ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={() => room.send({ type: 'offer_draw' })}
                      disabled={iOfferedDraw || livePly < 2}
                    >
                      <Handshake size={22} /> {iOfferedDraw ? t('game.drawOffered') : t('game.offerDraw')}
                    </button>
                    <button type="button" className="btn" onClick={() => setConfirmResign(true)}>
                      <Flag size={22} /> {t('game.resign')}
                    </button>
                  </>
                ) : (
                  <>
                    {rematchButton}
                    {state.matched ? (
                      <Link href="/" className="btn">
                        <Home size={22} /> {t('nav.home')}
                      </Link>
                    ) : (
                      <Link href="/friend" className="btn">
                        <Plus size={22} /> {t('room.newRoom')}
                      </Link>
                    )}
                  </>
                )}
              </div>
            )}

            <MovesFold>
              <MoveList
                sans={moves.map((m) => m.san)}
                ply={ply}
                onSelect={history.select}
                onFlip={() => setFlipped((f) => !f)}
              />
            </MovesFold>
          </>
        }
      />

      <ConfirmDialog
        open={confirmResign}
        title={t('confirm.resign.title')}
        body={t('confirm.resign.body')}
        confirmLabel={t('game.resign')}
        danger
        onCancel={() => setConfirmResign(false)}
        onConfirm={() => {
          setConfirmResign(false);
          room.send({ type: 'resign' });
        }}
      />

      <ConfirmDialog
        open={leaveTo !== null}
        title={t('leave.title')}
        body={t('leave.body', { seconds: graceSeconds })}
        confirmLabel={t('leave.confirm')}
        danger
        onCancel={() => setLeaveTo(null)}
        onConfirm={() => {
          const to = leaveTo;
          setLeaveTo(null);
          setLeaveGuard(null);
          if (to) navigate(to, { force: true });
        }}
      />

      <ResultDialog
        open={resultDialog.open}
        result={state.result}
        perspective={me}
        moveCount={state.moves.length}
        rating={myRating}
        reward={me !== null ? (state.rewards?.[me] ?? null) : null}
        players={resultNames}
        onClose={resultDialog.close}
        actions={
          <>
            {me !== null && rematchButton}
            {me !== null && !state.matched && (
              <Link href="/friend" className="btn">
                <Plus size={22} /> {t('room.newRoom')}
              </Link>
            )}
            <Link href="/" className={me === null ? 'btn btn-blue' : 'btn'}>
              <Home size={22} /> {t('result.menu')}
            </Link>
            <button type="button" className="btn btn-ghost btn-sm" onClick={resultDialog.close}>
              <Eye size={20} /> {t('result.review')}
            </button>
          </>
        }
      />

      {toast && (
        <div className="toast-region" aria-live="polite">
          <div className="toast">{toast}</div>
        </div>
      )}
    </>
  );
}

/**
 * Seconds `color` has left to come back before losing by forfeit, or null when
 * no countdown is running (connected, game over, or nobody left to award it to).
 */
function useForfeitCountdown(state: PublicRoomState, color: Color, receivedAt: number): number | null {
  const player = state.players[color];
  const other = state.players[color === 'w' ? 'b' : 'w'];
  // Count down from when this state arrived, so client/server clock skew does not matter.
  const [now, setNow] = useState(Date.now);
  const graceLeftAtReceipt =
    player && !player.connected && player.disconnectedAt !== null
      ? player.disconnectedAt + state.reconnectGraceMs - state.serverTime
      : null;
  const counting = graceLeftAtReceipt !== null && state.status === 'playing' && Boolean(other?.connected);

  useEffect(() => {
    if (!counting) return;
    // A stale `now` (from an earlier countdown) only counts as zero elapsed below.
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [counting, receivedAt]);

  if (!counting) return null;
  return Math.max(0, Math.ceil((graceLeftAtReceipt! - Math.max(0, now - receivedAt)) / 1000));
}

/** Online / offline indicator with the reconnection countdown. */
function PresenceBadge({ state, color, receivedAt }: { state: PublicRoomState; color: Color; receivedAt: number }) {
  const { t } = usePreferences();
  const player = state.players[color];
  const seconds = useForfeitCountdown(state, color, receivedAt);

  if (!player) return null;
  if (player.connected) {
    return (
      <span className="badge" title={t('room.online')}>
        <span className="status-dot online" />
        {t('room.online')}
      </span>
    );
  }
  if (seconds !== null) {
    return (
      <span className="badge">
        <span className="status-dot warning" />
        {t('room.disconnected', { seconds })}
      </span>
    );
  }
  return (
    <span className="badge">
      <span className="status-dot" />
      {t('room.offline')}
    </span>
  );
}
